import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Modal, Progress, Select, Table, Tag, Tooltip, Typography, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { DownloadOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import { ApolloError, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import AsyncReportDownloadModal, { dashboardAsyncReportLabelI18nKeys } from '../../../components/asyncReportDownload';
import { useFilteredPricingScenarios } from '../../../graphql/queries/settlement/getSettlementCommonQueries';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import {
    GET_PRE_BILL_REVISION_TASKS,
    GET_PRE_BILL_REVISION_UPLOAD_POLICY,
    GET_PRE_BILL_REVISION_TASK_ERROR_DETAIL,
    SUBMIT_PRE_BILL_REVISION_UPLOAD,
    ENQUEUE_PRE_BILL_REVISION_TASK_RESULT_REPORT
} from './preBillRevision/graphql';
import {
    isPreBillRevisionWeeklyWindowBlocked,
    PRE_BILL_REVISION_WEEKLY_SUBMISSION_HINT,
    PRE_BILL_REVISION_WEEKLY_WINDOW_BLOCKED_MESSAGE,
    preBillRevisionInProgressTaskBlockedMessage
} from './preBillRevision/preBillRevisionUploadPolicy';

const TEMPLATE_FILE_NAME = '出账前数据订正模版.xlsx';

/** 与 PreBillRevisionApplication.MAX_ROW_COUNT 一致：单任务有效运单号行上限 */
const PRE_BILL_REVISION_MAX_DATA_ROWS = 100_000;

const TRACKING_NUMBER_REGEX = /^[A-Za-z0-9_-]{1,128}$/;

/**
 * 统计「除去表头外、首列非空且运单号格式合法」的行数（与后端 parse 计数规则对齐），用于上传前拦截。
 * Excel 使用 sheetRows 限制解析深度，避免超大文件占满内存。
 */
const countValidPreBillRevisionDataRows = async (file: File): Promise<number> => {
    const lower = file.name.toLowerCase();
    if (lower.endsWith('.csv')) {
        const text = await file.text();
        const lines = text.split(/\r?\n/);
        let valid = 0;
        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].replace(/\r$/, '').trim();
            if (!line) {
                continue;
            }
            if (line.includes(',')) {
                continue;
            }
            if (!TRACKING_NUMBER_REGEX.test(line)) {
                continue;
            }
            valid++;
        }
        return valid;
    }
    const ab = await file.arrayBuffer();
    const wb = XLSX.read(ab, {
        type: 'array',
        cellDates: true,
        sheetRows: PRE_BILL_REVISION_MAX_DATA_ROWS + 2
    });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    if (!sheet?.['!ref']) {
        return 0;
    }
    const range = XLSX.utils.decode_range(sheet['!ref'] as string);
    let valid = 0;
    for (let R = range.s.r + 1; R <= range.e.r; R++) {
        const addr = XLSX.utils.encode_cell({ r: R, c: range.s.c });
        const cell = sheet[addr] as { w?: string; v?: unknown } | undefined;
        const raw = cell?.w ?? cell?.v;
        const v = raw == null ? '' : String(raw).trim();
        if (!v) {
            continue;
        }
        if (!TRACKING_NUMBER_REGEX.test(v)) {
            continue;
        }
        valid++;
    }
    return valid;
};

const resolvePreBillRevisionUploadErrorMessage = (err: unknown): string => {
    if (err instanceof ApolloError) {
        const gqlMessage = err.graphQLErrors?.[0]?.message;
        if (gqlMessage) {
            return gqlMessage;
        }
    }
    if (err instanceof Error && err.message) {
        return err.message;
    }
    return String(err);
};

const STATUS_PENDING = 'PENDING';
const STATUS_EXECUTING = 'EXECUTING';
const STATUS_VALIDATION_FAILED = 'VALIDATION_FAILED';
const STATUS_COMPLETED = 'COMPLETED';
const STATUS_FAILED = 'FAILED';

type PreBillRevisionTaskRow = {
    id: string;
    businessScenarioCode: number;
    operatorName: string;
    sourceFileName: string | null;
    totalCount: number;
    progressPercent: number;
    resultFileS3Key: string | null;
    resultFileName: string | null;
    repushTaskKey: string | null;
    status: string;
    failureReason: string | null;
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
};

const formatTaskDateTime = (iso: string | null | undefined): string => {
    if (!iso?.trim()) {
        return '—';
    }
    const d = dayjs(iso);
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : '—';
};

const statusTagColor = (status: string): string => {
    switch (status) {
        case STATUS_COMPLETED:
            return 'success';
        case STATUS_FAILED:
        case STATUS_VALIDATION_FAILED:
            return 'error';
        case STATUS_EXECUTING:
            return 'processing';
        case STATUS_PENDING:
        default:
            return 'default';
    }
};

const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const dataUrl = reader.result;
            if (typeof dataUrl !== 'string') {
                reject(new Error('Failed to read file'));
                return;
            }
            const comma = dataUrl.indexOf(',');
            if (comma === -1) {
                reject(new Error('Failed to read file'));
                return;
            }
            resolve(dataUrl.slice(comma + 1));
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });

const PreBillRevisionPage: React.FC = () => {
    const { t } = useTranslation();
    const [selectedScenarioCode, setSelectedScenarioCode] = useState<number | undefined>(undefined);
    const hasAppliedDefaultScenario = useRef(false);
    /** 防止连续选择文件触发重复上传（在 uploading 状态更新前） */
    const preBillRevisionUploadRunningRef = useRef(false);
    const [uploading, setUploading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorModalTaskId, setErrorModalTaskId] = useState<number | null>(null);

    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [downloadModalFileName, setDownloadModalFileName] = useState('');

    const [downloadingResultTaskId, setDownloadingResultTaskId] = useState<number | null>(null);

    const hasRevenueAccess = hasResource([ResourceCode.settlement.SETTLEMENT_REVENUE_PRE_BILL_REVISION]);
    const hasCostAccess = hasResource([ResourceCode.settlement.SETTLEMENT_COST_PRE_BILL_REVISION]);
    const canOperate = hasResource([
        ResourceCode.settlement.SETTLEMENT_REVENUE_PRE_BILL_REVISION_OPERATE,
        ResourceCode.settlement.SETTLEMENT_COST_PRE_BILL_REVISION_OPERATE,
    ]);
    const { scenarios, scenarioOptions, loading: scenarioLoading } = useFilteredPricingScenarios(hasRevenueAccess, hasCostAccess);
    const { data: tasksData, loading: tasksLoading, refetch: refetchTasks } = useQuery<{
        getPreBillRevisionTasks: PreBillRevisionTaskRow[];
    }>(GET_PRE_BILL_REVISION_TASKS, {
        variables: { businessScenarioCode: selectedScenarioCode ?? 0 },
        skip: selectedScenarioCode == null,
        fetchPolicy: 'cache-and-network'
    });

    const {
        data: uploadPolicyData,
        refetch: refetchUploadPolicy
    } = useQuery<{
        getPreBillRevisionUploadPolicy: {
            uploadAllowed: boolean;
            blockedByInProgressTask: boolean;
            inProgressTaskId: number | null;
            blockedByWeeklyWindow: boolean;
        };
    }>(GET_PRE_BILL_REVISION_UPLOAD_POLICY, {
        variables: { businessScenarioCode: selectedScenarioCode ?? 0 },
        skip: selectedScenarioCode == null,
        fetchPolicy: 'cache-and-network',
        pollInterval: 30_000
    });

    const [submitUpload] = useMutation(SUBMIT_PRE_BILL_REVISION_UPLOAD);
    const [enqueueTaskResultReport] = useMutation(ENQUEUE_PRE_BILL_REVISION_TASK_RESULT_REPORT);
    const [fetchErrorDetail, { data: errorDetailData, loading: errorDetailLoading }] = useLazyQuery<{
        getPreBillRevisionTaskErrorDetail: string | null;
    }>(GET_PRE_BILL_REVISION_TASK_ERROR_DETAIL, { fetchPolicy: 'network-only' });

    useEffect(() => {
        if (hasAppliedDefaultScenario.current || !scenarios.length) {
            return;
        }
        // Long 标量常被 Apollo 序列化为字符串，需与 scenarioOptions 的 number value 对齐，否则 Select 匹配不到选项会直接展示 code
        setSelectedScenarioCode(Number(scenarios[0].businessScenarioCode));
        hasAppliedDefaultScenario.current = true;
    }, [scenarios]);

    useEffect(() => {
        setPagination((p) => ({ ...p, current: 1 }));
    }, [selectedScenarioCode]);

    const tasks = tasksData?.getPreBillRevisionTasks ?? [];

    const uploadPolicy = uploadPolicyData?.getPreBillRevisionUploadPolicy;
    const weeklyWindowBlocked = isPreBillRevisionWeeklyWindowBlocked();
    const inProgressTaskBlocked = uploadPolicy?.blockedByInProgressTask ?? false;
    const inProgressTaskId = uploadPolicy?.inProgressTaskId;
    const uploadDisabled =
        uploading ||
        selectedScenarioCode === undefined ||
        weeklyWindowBlocked ||
        inProgressTaskBlocked;

    const uploadDisabledReason = useMemo(() => {
        if (weeklyWindowBlocked) {
            return PRE_BILL_REVISION_WEEKLY_WINDOW_BLOCKED_MESSAGE;
        }
        if (inProgressTaskBlocked) {
            return preBillRevisionInProgressTaskBlockedMessage(inProgressTaskId ?? '—');
        }
        if (selectedScenarioCode === undefined) {
            return t('central.revise.preBillRevisionPage.selectScenarioFirst');
        }
        return '';
    }, [weeklyWindowBlocked, inProgressTaskBlocked, inProgressTaskId, selectedScenarioCode]);

    const refetchTasksAndPolicy = () => {
        void refetchTasks();
        void refetchUploadPolicy();
    };

    const handleDownloadTemplate = () => {
        const worksheet = XLSX.utils.aoa_to_sheet([['trackingNumber']]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', TEMPLATE_FILE_NAME);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        message.success(t('settlement.revise.preBillRevisionPage.templateDownloaded'));
    };

    const runUpload = async (file: File) => {
        if (selectedScenarioCode === undefined) {
            message.error(t('settlement.revise.preBillRevisionPage.selectScenarioFirst'));
            return;
        }
        if (weeklyWindowBlocked) {
            message.error(PRE_BILL_REVISION_WEEKLY_WINDOW_BLOCKED_MESSAGE);
            return;
        }
        if (inProgressTaskBlocked) {
            message.error(preBillRevisionInProgressTaskBlockedMessage(inProgressTaskId ?? '—'));
            return;
        }
        const lower = file.name.toLowerCase();
        if (!lower.endsWith('.xlsx') && !lower.endsWith('.xls') && !lower.endsWith('.csv')) {
            message.error(t('settlement.revise.preBillRevisionPage.invalidFileType'));
            return;
        }
        let validRowCount = 0;
        try {
            validRowCount = await countValidPreBillRevisionDataRows(file);
        } catch {
            message.error(t('settlement.revise.preBillRevisionPage.rowCountCheckFailed'));
            return;
        }
        if (validRowCount > PRE_BILL_REVISION_MAX_DATA_ROWS) {
            message.error(
                t('settlement.revise.preBillRevisionPage.rowCountExceeded', {
                    max: PRE_BILL_REVISION_MAX_DATA_ROWS.toLocaleString()
                })
            );
            return;
        }
        const confirmed = await new Promise<boolean>((resolve) => {
            Modal.confirm({
                title: t('settlement.revise.preBillRevisionPage.confirmUploadTitle'),
                content: t('settlement.revise.preBillRevisionPage.confirmUploadContent'),
                okText: t('settlement.revise.preBillRevisionPage.confirmOk'),
                cancelText: t('settlement.revise.preBillRevisionPage.confirmCancel'),
                onOk: () => resolve(true),
                onCancel: () => resolve(false)
            });
        });
        if (!confirmed) {
            return;
        }
        setUploading(true);
        message.loading({
            content: t('settlement.revise.preBillRevisionPage.uploading'),
            key: 'pre-bill-revision-upload',
            duration: 0
        });
        try {
            const fileBase64 = await readFileAsBase64(file);
            const res = await submitUpload({
                variables: {
                    businessScenarioCode: selectedScenarioCode,
                    fileBase64,
                    fileName: file.name
                }
            });
            message.destroy('pre-bill-revision-upload');
            const taskId = res.data?.submitPreBillRevisionUpload;
            message.success(t('settlement.revise.preBillRevisionPage.uploadSuccess', { taskId }));
            refetchTasksAndPolicy();
        } catch (err: unknown) {
            message.destroy('pre-bill-revision-upload');
            message.error(resolvePreBillRevisionUploadErrorMessage(err) || t('settlement.revise.preBillRevisionPage.uploadFailed'));
            refetchTasksAndPolicy();
        } finally {
            setUploading(false);
        }
    };

    const handleBeforeUpload = (file: File) => {
        if (uploading || preBillRevisionUploadRunningRef.current) {
            return false;
        }
        preBillRevisionUploadRunningRef.current = true;
        void (async () => {
            try {
                await runUpload(file);
            } finally {
                preBillRevisionUploadRunningRef.current = false;
            }
        })();
        return false;
    };

    const formatStatus = (status: string) => {
        switch (status) {
            case STATUS_PENDING:
                return t('settlement.revise.preBillRevisionPage.status.parsing');
            case STATUS_EXECUTING:
                return t('settlement.revise.preBillRevisionPage.status.executing');
            case STATUS_VALIDATION_FAILED:
                return t('settlement.revise.preBillRevisionPage.status.validationFailed');
            case STATUS_COMPLETED:
                return t('settlement.revise.preBillRevisionPage.status.completed');
            case STATUS_FAILED:
                return t('settlement.revise.preBillRevisionPage.status.failed');
            default:
                return status;
        }
    };

    const openErrorModal = (taskId: number) => {
        setErrorModalTaskId(taskId);
        setErrorModalOpen(true);
        fetchErrorDetail({ variables: { taskId } });
    };

    const downloadTaskResultFile = async (record: PreBillRevisionTaskRow) => {
        const idNum = Number(record.id);
        setDownloadingResultTaskId(idNum);
        try {
            const { data } = await enqueueTaskResultReport({
                variables: { preBillRevisionTaskId: idNum }
            });
            const reportId = data?.enqueuePreBillRevisionTaskResultReport;
            if (reportId == null) {
                message.error(t('settlement.revise.preBillRevisionPage.resultDownloadUnavailable'));
                return;
            }
            const name =
                record.resultFileName?.trim() ||
                `pre-bill-revision-task-${idNum}-details.xlsx`;
            setDownloadModalFileName(name);
            setDownloadModalVisible(true);
        } catch (err: unknown) {
            message.error(resolvePreBillRevisionUploadErrorMessage(err) || t('settlement.revise.preBillRevisionPage.resultDownloadFailed'));
        } finally {
            setDownloadingResultTaskId(null);
        }
    };

    const canShowResultDownload = (record: PreBillRevisionTaskRow) =>
        record.status === STATUS_COMPLETED &&
        record.progressPercent === 100 &&
        Boolean(record.resultFileS3Key?.trim() || record.resultFileName?.trim());

    const columns: ColumnsType<PreBillRevisionTaskRow> = [
        {
            title: t('settlement.revise.preBillRevisionPage.columns.taskNo'),
            key: 'taskNo',
            dataIndex: 'id',
            width: 120,
            align: 'center',
            ellipsis: true,
            render: (id: string) => {
                const text = id?.trim();
                if (!text) {
                    return <span className="text-neutral-400">—</span>;
                }
                return (
                    <span
                        className="inline-block max-w-full truncate rounded bg-neutral-100 px-2 py-0.5 font-mono text-xs text-neutral-800 tabular-nums"
                        title={text}
                    >
                        {text}
                    </span>
                );
            }
        },
        {
            title: t('settlement.revise.preBillRevisionPage.columns.fileName'),
            dataIndex: 'sourceFileName',
            key: 'sourceFileName',
            width: 200,
            align: 'center',
            ellipsis: true,
            render: (v: string | null | undefined) => (
                <span className="text-neutral-800" title={v ?? undefined}>
                    {v?.trim() ? v : '—'}
                </span>
            )
        },
        {
            title: t('settlement.revise.preBillRevisionPage.columns.operator'),
            dataIndex: 'operatorName',
            key: 'operatorName',
            width: 120,
            align: 'center',
            ellipsis: true
        },
        {
            title: t('settlement.revise.preBillRevisionPage.columns.repushTaskKey'),
            dataIndex: 'repushTaskKey',
            key: 'repushTaskKey',
            width: 220,
            align: 'center',
            ellipsis: true,
            render: (v: string | null | undefined) => {
                const text = v?.trim();
                if (!text) {
                    return <span className="text-neutral-400">—</span>;
                }
                return (
                    <span
                        className="inline-block max-w-full truncate font-mono text-xs text-neutral-800"
                        title={text}
                    >
                        {text}
                    </span>
                );
            }
        },
        {
            title: t('settlement.revise.preBillRevisionPage.columns.totalCount'),
            dataIndex: 'totalCount',
            key: 'totalCount',
            width: 88,
            align: 'center',
            render: (v: number) => <span className="tabular-nums text-neutral-800">{v}</span>
        },
        {
            title: t('settlement.revise.preBillRevisionPage.columns.createdAt'),
            key: 'createdAt',
            width: 168,
            align: 'center',
            render: (_: unknown, record: PreBillRevisionTaskRow) => (
                <span className="inline-block whitespace-nowrap font-mono text-xs text-neutral-700 tabular-nums">
                    {formatTaskDateTime(record.createdAt)}
                </span>
            )
        },
        {
            title: t('settlement.revise.preBillRevisionPage.columns.startedAt'),
            key: 'startedAt',
            width: 168,
            align: 'center',
            render: (_: unknown, record: PreBillRevisionTaskRow) => (
                <span className="inline-block whitespace-nowrap font-mono text-xs text-neutral-700 tabular-nums">
                    {formatTaskDateTime(record.startedAt)}
                </span>
            )
        },
        {
            title: t('settlement.revise.preBillRevisionPage.columns.completedAt'),
            key: 'completedAt',
            width: 168,
            align: 'center',
            render: (_: unknown, record: PreBillRevisionTaskRow) => (
                <span className="inline-block whitespace-nowrap font-mono text-xs text-neutral-700 tabular-nums">
                    {formatTaskDateTime(record.completedAt)}
                </span>
            )
        },
        {
            title: t('settlement.revise.preBillRevisionPage.columns.progress'),
            key: 'progress',
            width: 176,
            align: 'center',
            render: (_: unknown, record: PreBillRevisionTaskRow) => (
                <div className="mx-auto flex w-full min-w-[140px] max-w-[168px] flex-col items-center gap-1">
                    <Tag color={statusTagColor(record.status)} className="m-0">
                        {formatStatus(record.status)}
                    </Tag>
                    <div className="w-full px-0.5">
                        <Progress percent={record.progressPercent ?? 0} size="small" strokeColor="#1677ff" />
                    </div>
                </div>
            )
        },
        {
            title: t('settlement.revise.preBillRevisionPage.columns.actions'),
            key: 'actions',
            width: 112,
            align: 'center',
            fixed: 'right',
            render: (_: unknown, record: PreBillRevisionTaskRow) => {
                const idNum = Number(record.id);
                if (record.status === STATUS_VALIDATION_FAILED) {
                    return (
                        <Button type="link" size="small" className="px-0" onClick={() => openErrorModal(idNum)}>
                            {t('settlement.revise.preBillRevisionPage.errorDetail')}
                        </Button>
                    );
                }
                if (canShowResultDownload(record) && canOperate) {
                    return (
                        <Button
                            type="link"
                            size="small"
                            className="px-0"
                            loading={downloadingResultTaskId === idNum}
                            onClick={() => downloadTaskResultFile(record)}
                        >
                            {t('settlement.revise.preBillRevisionPage.executionResult')}
                        </Button>
                    );
                }
                return <span className="text-neutral-300">—</span>;
            }
        }
    ];

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <Typography.Title level={4} style={{ margin: 0 }}>
                    {t('settlement.revise.preBillRevisionPage.title')}
                </Typography.Title>
                <div className="flex flex-wrap items-center gap-3 shrink-0">
                    <span className="text-gray-600 whitespace-nowrap">
                        {t('settlement.revise.preBillRevisionPage.businessScenario')}
                    </span>
                    <Select
                        className="min-w-[200px]"
                        placeholder={t('settlement.revise.preBillRevisionPage.selectScenario')}
                        loading={scenarioLoading}
                        options={scenarioOptions}
                        value={selectedScenarioCode}
                        onChange={(v) => setSelectedScenarioCode(v)}
                        allowClear
                    />
                </div>
            </div>

            <Alert
                type="warning"
                showIcon
                className="mb-4"
                message="使用提示"
                description={
                    <ul className="mb-0 list-disc space-y-1 pl-5 text-sm">
                        <li>
                            文件须为单列，表头为 trackingNumber；运单号仅含字母、数字、下划线、短横线，长度 1～128；除去表头外有效运单号不超过{' '}
                            {PRE_BILL_REVISION_MAX_DATA_ROWS.toLocaleString()} 条。
                        </li>
                        <li>
                            <span className="font-semibold text-red-600">
                                同一文件内请勿重复填写同一运单号，否则会与任务明细唯一约束冲突导致上传失败。
                            </span>
                        </li>
                        <li>支持 .xlsx、.xls、.csv；建议先下载模板再填写上传。</li>
                        <li>任务执行完成后，当进度为 100% 且已生成结果文件时，可在列表中点击「执行结果」，在「下载中心」下载 Excel。</li>
                        <li>查看任务进度请点击任务列表旁的「进度刷新」。只会有一个在处理中的任务。</li>
                        <li>
                            <span className="font-semibold text-red-600">{PRE_BILL_REVISION_WEEKLY_SUBMISSION_HINT}</span>
                        </li>
                    </ul>
                }
            />

            <div className="flex flex-wrap gap-2 mb-4">
                {canOperate && (
                    <Tooltip
                        title={
                            uploadDisabled && uploadDisabledReason
                                ? uploadDisabledReason
                                : PRE_BILL_REVISION_WEEKLY_SUBMISSION_HINT
                        }
                    >
                    <span className="inline-block">
                        <Upload
                            accept=".xlsx,.xls,.csv"
                            showUploadList={false}
                            beforeUpload={handleBeforeUpload}
                            disabled={uploadDisabled}
                        >
                            <Button
                                type="primary"
                                icon={<UploadOutlined />}
                                loading={uploading}
                                disabled={uploadDisabled}
                            >
                                {t('settlement.revise.preBillRevisionPage.uploadWaybillList')}
                            </Button>
                        </Upload>
                    </span>
                    </Tooltip>
                )}
                <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
                    {t('settlement.revise.preBillRevisionPage.downloadTemplate')}
                </Button>
            </div>

            <Card
                title={t('settlement.revise.preBillRevisionPage.taskList')}
                extra={
                    <Button icon={<ReloadOutlined />} onClick={() => refetchTasksAndPolicy()} loading={tasksLoading}>
                        {t('settlement.revise.preBillRevisionPage.refresh')}
                    </Button>
                }
            >
                <Table<PreBillRevisionTaskRow>
                    rowKey="id"
                    columns={columns}
                    dataSource={tasks}
                    loading={tasksLoading}
                    size="small"
                    bordered={false}
                    rowClassName={(_, index) => (index % 2 === 1 ? 'bg-neutral-50/80' : '')}
                    className="pre-bill-revision-task-table [&_.ant-table-thead>tr>th]:bg-neutral-100/90 [&_.ant-table-thead>tr>th]:text-center [&_.ant-table-thead>tr>th]:text-neutral-600 [&_.ant-table-thead>tr>th]:font-medium [&_.ant-table-thead>tr>th]:text-xs [&_.ant-table-tbody>tr>td]:align-middle [&_.ant-table-tbody>tr>td]:py-2.5"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        showSizeChanger: true,
                        onChange: (current, pageSize) => {
                            setPagination({ current, pageSize: pageSize || 10 });
                        },
                        showTotal: (total) => t('settlement.revise.preBillRevisionPage.totalTasks', { total })
                    }}
                    scroll={{ x: 1300 }}
                />
            </Card>

            <Modal
                open={errorModalOpen}
                onCancel={() => setErrorModalOpen(false)}
                title={t('settlement.revise.preBillRevisionPage.errorDetailTitle', { taskId: errorModalTaskId ?? '' })}
                footer={null}
                width={560}
            >
                {errorDetailLoading ? (
                    <div className="py-6 text-center text-gray-500">{t('settlement.revise.preBillRevisionPage.loading')}</div>
                ) : (
                    <Typography.Paragraph className="whitespace-pre-wrap mb-0">
                        {errorDetailData?.getPreBillRevisionTaskErrorDetail ||
                            t('settlement.revise.preBillRevisionPage.noErrorDetail')}
                    </Typography.Paragraph>
                )}
            </Modal>

            <AsyncReportDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={downloadModalFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.preBillRevisionTaskDetailExport}
            />
        </div>
    );
};

export default PreBillRevisionPage;
